import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Play, Radio, Clock, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

const CADENCE_TONE = {
  manual: "text-ivory/60 border-ivory/25",
  hourly: "text-emerald-300 border-emerald-500/40",
  daily: "text-gold border-gold/40",
  weekly: "text-bronze border-bronze/40",
  monthly: "text-ivory/70 border-ivory/30",
};

// Map adapter key → the source_domain to filter search_results by
const DOMAIN_FOR = {
  gazette: "thegazette.co.uk",
};

function oppTone(s) {
  const v = Number(s) || 0;
  if (v >= 75) return "text-emerald-300 border-emerald-500/50";
  if (v >= 60) return "text-gold border-gold/50";
  if (v >= 40) return "text-bronze border-bronze/50";
  return "text-ruby-light border-ruby/50";
}

export default function DataPipelines() {
  const [pipelines, setPipelines] = useState([]);
  const [runningKey, setRunningKey] = useState(null);
  const [expanded, setExpanded] = useState({});   // key → bool
  const [results, setResults] = useState({});     // key → rows
  const [loadingResults, setLoadingResults] = useState({});

  const load = () => api.get("/pipelines").then((r) => setPipelines(r.data || []));
  useEffect(() => { load(); }, []);

  const fetchResults = async (key) => {
    const domain = DOMAIN_FOR[key];
    if (!domain) return;
    setLoadingResults((c) => ({ ...c, [key]: true }));
    try {
      const { data } = await api.get(`/search-results?source_domain=${domain}&limit=25`);
      setResults((c) => ({ ...c, [key]: data.rows || [] }));
    } catch {
      toast.error("Failed to load results");
    } finally {
      setLoadingResults((c) => ({ ...c, [key]: false }));
    }
  };

  const toggle = async (key) => {
    const next = !expanded[key];
    setExpanded((c) => ({ ...c, [key]: next }));
    if (next && !results[key]) await fetchResults(key);
  };

  const run = async (key) => {
    setRunningKey(key);
    const t = toast.loading(`Running ${key}…`);
    try {
      const { data } = await api.post(`/pipelines/${key}/run`);
      toast.success(
        `${key}: ${data.inserted} new · ${data.duplicates} duplicates (${data.total} fetched)`,
        { id: t, duration: 6000 }
      );
      await load();
      // Auto-expand + refresh results so the user sees them immediately
      setExpanded((c) => ({ ...c, [key]: true }));
      await fetchResults(key);
    } catch (e) {
      toast.error(`${key} failed`, { id: t });
    } finally {
      setRunningKey(null);
    }
  };

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Data Pipelines</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Own the data</h1>
      <p className="mt-3 text-ivory/60 max-w-2xl text-sm !leading-[1.7]">
        Every result these adapters return is scored and lands in <Link to="/admin/new-finds" className="text-gold hover:underline">New Finds</Link> alongside your SerpAPI runs — one inbox for every source.
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="label-mono-mute">Configured adapters</p>
        <button
          onClick={async () => {
            const t = toast.loading("Rescoring every stored find…");
            try {
              const { data } = await api.post("/pipelines/rescore-search-results");
              toast.success(`Rescored ${data.updated} results`, { id: t });
              // Refresh currently-expanded panels
              for (const k of Object.keys(expanded)) if (expanded[k]) await fetchResults(k);
            } catch { toast.error("Rescore failed", { id: t }); }
          }}
          className="btn-outline !py-1.5 !px-3 !text-[0.65rem]"
          data-testid="rescore-search-results-btn"
        >
          Rescore all finds
        </button>
      </div>

      <div className="space-y-3" data-testid="pipelines-list">
        {pipelines.length === 0 && (
          <div className="luxury-card p-8 text-center text-ivory/40 text-sm">No pipelines configured.</div>
        )}

        {pipelines.map((p) => {
          const tone = CADENCE_TONE[p.cadence] || CADENCE_TONE.manual;
          const busy = runningKey === p.key;
          const isOpen = !!expanded[p.key];
          const rows = results[p.key] || [];
          const canExpand = !!DOMAIN_FOR[p.key];

          return (
            <div key={p.key} className="luxury-card overflow-hidden" data-testid={`pipeline-${p.key}`}>
              {/* Header */}
              <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Radio size={14} className="text-ruby-light shrink-0" />
                    <h3 className="text-lg text-ivory font-serif">{p.label}</h3>
                    <span className={`text-[0.6rem] uppercase tracking-[0.15em] border px-1.5 py-0.5 ${tone}`}>
                      {p.cadence}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-ivory/50 flex items-center gap-4 flex-wrap">
                    <span className="font-mono">{p.key}</span>
                    {p.last_run ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} /> Last run {new Date(p.last_run).toLocaleString("en-GB",
                          { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {" · "}<span className="text-gold">{p.last_inserted}</span> new
                        {" · "}<span className="text-ivory/40">{p.last_duplicates}</span> dupes
                      </span>
                    ) : (
                      <span className="text-ivory/40">Never run</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canExpand && (
                    <button
                      onClick={() => toggle(p.key)}
                      className="btn-outline !py-1.5 !px-3 !text-[0.65rem]"
                      data-testid={`toggle-pipeline-${p.key}`}
                    >
                      {isOpen ? <><ChevronUp size={12}/> Hide</> : <><ChevronDown size={12}/> View results</>}
                    </button>
                  )}
                  <button
                    onClick={() => run(p.key)}
                    disabled={busy}
                    className="btn-ruby disabled:opacity-50"
                    data-testid={`run-pipeline-${p.key}`}
                  >
                    {busy ? <><Loader2 size={13} className="animate-spin" /> Running…</> : <><Play size={13} /> Run now</>}
                  </button>
                </div>
              </div>

              {/* Inline results */}
              {isOpen && (
                <div className="border-t border-charcoal-600/60" data-testid={`pipeline-results-${p.key}`}>
                  {loadingResults[p.key] ? (
                    <div className="p-8 text-center text-ivory/40 text-sm inline-flex items-center gap-2 w-full justify-center">
                      <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                  ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-ivory/40 text-sm">
                      No stored results yet. Hit <span className="text-gold">Run now</span> to pull the latest.
                    </div>
                  ) : (
                    <>
                      <div className="p-3 text-[0.65rem] uppercase tracking-[0.15em] text-ivory/40 flex items-center justify-between">
                        <span>{rows.length} results · sorted by opportunity score</span>
                        <Link to="/admin/new-finds" className="text-gold hover:underline inline-flex items-center gap-1">
                          Open all in New Finds <ExternalLink size={10} />
                        </Link>
                      </div>
                      <div className="divide-y divide-charcoal-600/60 max-h-[540px] overflow-y-auto">
                        {rows.map((r) => (
                          <div key={r.id} className="p-4 hover:bg-charcoal-800/40 flex items-start gap-3" data-testid={`result-${r.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-ivory text-sm !leading-snug">{r.title}</p>
                              <div className="mt-1 text-[0.7rem] text-ivory/50 flex items-center gap-3 flex-wrap font-mono">
                                {r.possible_location && <span>{r.possible_location}</span>}
                                {r.date_found && (
                                  <span>{new Date(r.date_found).toLocaleDateString("en-GB", { day:"numeric", month:"short" })}</span>
                                )}
                              </div>
                              {r.snippet && (
                                <p className="mt-2 text-xs text-ivory/45 !leading-relaxed line-clamp-2">{r.snippet}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              {r.opportunity_score ? (
                                <span
                                  className={`font-mono text-[0.7rem] font-medium px-2 py-0.5 border ${oppTone(r.opportunity_score)}`}
                                  title={`Risk: ${r.risk_score || 0}`}
                                >
                                  {r.opportunity_score}
                                </span>
                              ) : null}
                              {r.source_url && (
                                <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                                   className="text-[0.65rem] uppercase tracking-[0.15em] text-gold/80 hover:text-gold inline-flex items-center gap-1">
                                  Open <ExternalLink size={9} />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 luxury-card p-6 border-l-2 border-gold">
        <p className="overline text-gold">Coming next</p>
        <h2 className="mt-2 text-xl font-serif text-ivory">Adapters on the roadmap</h2>
        <ul className="mt-3 text-sm text-ivory/70 space-y-1 !leading-[1.8]">
          <li>• <span className="text-ivory">Land Registry Price Paid</span> — sold-price history for every UK property since 1995</li>
          <li>• <span className="text-ivory">EPC Register</span> — energy rating + full address on 20M+ properties</li>
          <li>• <span className="text-ivory">Auction house RSS</span> — Barnards, SDL, Allsop, Cliveemson</li>
          <li>• <span className="text-ivory">PlanningAlerts</span> — planning applications matching your saved-search postcodes</li>
        </ul>
      </div>
    </Container>
  );
}
